import { useEffect } from 'react'

import type { History, Location } from 'history'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import { useAccessToken, useManagedAccessToken } from '@/app/AuthProvider'
import { checkAccessToken } from '@store/auth/login/actions'
import type { RootState } from '@store/rootReducer'

/**
 * Route state this hook reads, set by the `history.replace` call below.
 * `history@5`'s `Location.state` is `unknown`, so it has to be narrowed.
 */
const hasNoAuthCheck = (state: unknown): boolean =>
  typeof state === 'object' &&
  state !== null &&
  'noAuthCheck' in state &&
  !!state.noAuthCheck

const useAuthCheck = (requireAuth = true) => {
  const accessToken = useAccessToken()
  const isLoggedIn = !!accessToken
  const { initLoadComplete } = useSelector(({ ui }: RootState) => ui)
  const dispatch = useDispatch()
  // react-router-dom's v5 types don't resolve under tsgolint, so these hooks come
  // back as error types. Assert the `history` package's own types.
  const location = useLocation() as Location
  const history = useHistory() as History

  const managedAccessToken = useManagedAccessToken()
  useEffect(() => {
    if (!managedAccessToken) {
      return
    }
    const params = location.search
    const noAuthCheck = hasNoAuthCheck(location.state)

    if (requireAuth && !noAuthCheck && !isLoggedIn) {
      dispatch(
        checkAccessToken(
          {
            tokenOnly: isLoggedIn,
            redirectToLogin: requireAuth,
            redirectAfterLogin: `${location.pathname}${params}`,
          },
          history,
          managedAccessToken
        )
      )
    } else if (noAuthCheck) {
      history.replace(`${location.pathname}${params}`, {
        noAuthCheck: false,
      })
    } else if (!initLoadComplete) {
      dispatch(
        checkAccessToken(
          {
            location,
            tokenOnly: isLoggedIn,
            redirectToLogin: requireAuth,
            redirectAfterLogin: `${location.pathname}${params}`,
          },
          history,
          managedAccessToken
        )
      )
    }
  }, [])

  const loading = !initLoadComplete || (!isLoggedIn && requireAuth)

  return loading
}

export default useAuthCheck
